import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  DataTable,
  Field,
  Input,
  Modal,
  StateBlock,
  useToast,
  type Column,
} from '@w7/ui'
import { useMemo, useState, type FormEvent } from 'react'
import {
  cmApi,
  type SecurityOddsViolationWhitelistRow,
} from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import {
  canOddsViolationWhitelistAdd,
  canOddsViolationWhitelistRemove,
} from '@/app/staffAccess'
import { useCmMe } from '@/api/hooks/useCmMe'
import { CmPanel } from '@/features/catalog/CmPanel'

/**
 * Lab `/odds-violation-whitelist` — CM Security page.
 * Search toolbar + Add User modal (same chrome as All cheaters).
 */
export function OddsViolationWhitelistPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const me = useCmMe()
  const canAdd = canOddsViolationWhitelistAdd(me.data?.caller)
  const canRemove = canOddsViolationWhitelistRemove(me.data?.caller)

  const [page, setPage] = useState(1)
  const [userName, setUserName] = useState('')
  const [userNameDraft, setUserNameDraft] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const [addUserName, setAddUserName] = useState('')
  const [addRemark, setAddRemark] = useState('')
  const [pendingRemove, setPendingRemove] =
    useState<SecurityOddsViolationWhitelistRow | null>(null)
  const pageSize = 50

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['cm', 'security', 'odds-violation-whitelist'] })

  const resetAdd = () => {
    setAddOpen(false)
    setAddUserName('')
    setAddRemark('')
  }

  const listQ = useQuery({
    queryKey: [
      'cm',
      'security',
      'odds-violation-whitelist',
      page,
      pageSize,
      userName,
    ],
    queryFn: () =>
      cmApi.securityOddsViolationWhitelistList(authHeaders(), {
        page,
        pageSize,
        userName: userName || undefined,
      }),
  })

  const addM = useMutation({
    mutationFn: () => {
      if (!addUserName.trim()) throw new Error('Username is required')
      return cmApi.securityOddsViolationWhitelistAdd(authHeaders(), {
        userName: addUserName.trim(),
        remark: addRemark.trim() || undefined,
      })
    },
    onSuccess: async (res) => {
      toast.success(res.message || 'User added successfully')
      resetAdd()
      setPage(1)
      await invalidate()
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : String(err)),
  })

  const removeM = useMutation({
    mutationFn: (row: SecurityOddsViolationWhitelistRow) =>
      cmApi.securityOddsViolationWhitelistRemove(authHeaders(), { id: row.id }),
    onSuccess: async (res) => {
      toast.success(res.message || 'User removed successfully')
      setPendingRemove(null)
      await invalidate()
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : String(err)),
  })

  const rows = listQ.data?.rows ?? []
  const total = listQ.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const columns = useMemo((): Column<SecurityOddsViolationWhitelistRow>[] => {
    return [
      {
        id: 'n',
        header: '#',
        width: 48,
        numeric: true,
        cell: (_row, i) => (page - 1) * pageSize + i + 1,
      },
      { id: 'username', header: 'Username', cell: (r) => r.username },
      {
        id: 'remark',
        header: 'Remark',
        cell: (r) => r.remark?.trim() || '—',
      },
      {
        id: 'createdBy',
        header: 'Created By',
        cell: (r) => r.createdByUsername || r.createdBy || '—',
      },
      {
        id: 'createdOn',
        header: 'Created On',
        cell: (r) => {
          try {
            return new Date(r.createdAt).toLocaleString()
          } catch {
            return r.createdAt
          }
        },
      },
      {
        id: 'action',
        header: 'Action',
        cell: (r) =>
          canRemove ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              busy={removeM.isPending && pendingRemove?.id === r.id}
              aria-label={`Remove ${r.username}`}
              onClick={() => setPendingRemove(r)}
            >
              Remove
            </Button>
          ) : (
            '—'
          ),
      },
    ]
  }, [page, pageSize, canRemove, pendingRemove?.id, removeM.isPending])

  if (listQ.isLoading && !rows.length) {
    return (
      <StateBlock icon="settings" title="Loading Odds Violation Whitelist…">
        Reading CM-API security lists.
      </StateBlock>
    )
  }

  return (
    <CmPanel
      title="Odds Violation Whitelist"
      subtitle="Players skipped for soccer/tennis/cricket odds-violation lock"
      actions={
        <Button
          type="button"
          variant="secondary"
          busy={listQ.isFetching}
          onClick={() => void listQ.refetch()}
        >
          Refresh
        </Button>
      }
    >
      <div className="cm-security">
        <form
          className="cm-toolbar"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            const next = userNameDraft.trim()
            setPage(1)
            setUserName(next)
            // Always hit the network — same filter must not look "dead" in DevTools.
            void qc.invalidateQueries({
              queryKey: ['cm', 'security', 'odds-violation-whitelist'],
            })
          }}
        >
          <div className="cm-toolbar-search">
            <Field label="Search Username">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={userNameDraft}
                  maxLength={100}
                  onChange={(e) => {
                    const v = e.target.value
                    setUserNameDraft(v)
                    if (!v.trim()) {
                      setPage(1)
                      setUserName('')
                    }
                  }}
                  placeholder="Filter whitelist by username…"
                />
              )}
            </Field>
          </div>
          <Button type="submit" variant="secondary" busy={listQ.isFetching}>
            Search
          </Button>
          {canAdd ? (
            <Button type="button" onClick={() => setAddOpen(true)}>
              Add User
            </Button>
          ) : null}
        </form>

        {listQ.isError ? (
          <div className="cm-hint" role="alert">
            <p>{(listQ.error as Error).message}</p>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void listQ.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : null}

        {!listQ.isError && !listQ.isFetching ? (
          <p className="cm-hint">
            {userName
              ? `${total} match(es) for “${userName}”`
              : `${total} whitelisted player(s)`}
          </p>
        ) : null}

        <DataTable
          label="Odds violation whitelist"
          density="compact"
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={listQ.isFetching && !rows.length}
          empty={
            !listQ.isError ? (
              <div className="cm-security-empty">
                <StateBlock
                  icon="search"
                  title={
                    userName
                      ? `No matches for “${userName}”`
                      : 'Whitelist is empty'
                  }
                >
                  {userName
                    ? 'Search only filters players already on this list — Add User first if they are missing.'
                    : canAdd
                      ? 'Lab Mongo rows were not imported. Use Add User to whitelist a downline player.'
                      : 'No players on the odds violation whitelist yet.'}
                </StateBlock>
              </div>
            ) : null
          }
        />

        {total > pageSize ? (
          <div className="cm-security-pager">
            <Button
              type="button"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="cm-hint">
              Page {page} / {totalPages} · {total} row(s)
            </span>
            <Button
              type="button"
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </div>

      <Modal
        open={addOpen}
        onClose={() => {
          if (addM.isPending) return
          resetAdd()
        }}
        title="Add to odds violation whitelist"
        closeDisabled={addM.isPending}
        footer={
          <div className="cm-form-actions">
            <Button
              type="button"
              variant="secondary"
              disabled={addM.isPending}
              onClick={resetAdd}
            >
              Cancel
            </Button>
            <Button
              type="button"
              busy={addM.isPending}
              disabled={!addUserName.trim()}
              onClick={() => addM.mutate()}
            >
              Add User
            </Button>
          </div>
        }
      >
        <div className="cm-form">
          <Field label="Username" required>
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                required
                autoFocus
                value={addUserName}
                maxLength={100}
                onChange={(e) => setAddUserName(e.target.value)}
                placeholder="Enter username"
              />
            )}
          </Field>
          <Field label="Remark">
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={addRemark}
                maxLength={500}
                onChange={(e) => setAddRemark(e.target.value)}
                placeholder="Optional remark"
              />
            )}
          </Field>
        </div>
      </Modal>

      <Modal
        open={!!pendingRemove}
        onClose={() => {
          if (removeM.isPending) return
          setPendingRemove(null)
        }}
        title="Remove from whitelist"
        subtitle={
          pendingRemove ? `Player: ${pendingRemove.username}` : undefined
        }
        tone="danger"
        closeDisabled={removeM.isPending}
        footer={
          <div className="cm-form-actions">
            <Button
              type="button"
              variant="secondary"
              disabled={removeM.isPending}
              onClick={() => setPendingRemove(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              busy={removeM.isPending}
              onClick={() => {
                if (pendingRemove) removeM.mutate(pendingRemove)
              }}
            >
              Remove
            </Button>
          </div>
        }
      >
        <p className="cm-hint">
          This clears the odds-violation whitelist flag for this player.
        </p>
      </Modal>
    </CmPanel>
  )
}
